import React from 'react'
import NavigationAnchor from '../Component/EnhancedData/NavigationAnchor'
import { RowDataWithKey } from '../Component/EnhancedData/SortableTable/Types'
import { CheckHollowIcon, CheckSolidIcon, WarningSolidIcon } from '../Component/Icons'
import * as EDT from '../Type/EnhancedData'
import { MANAGE_ENHANCED_DATA_PAGE_ROUTE } from './EnhancedData/Constants'

const buildNavigationURLForMEDPage = (transaction_id?: string): string => {
  if (!transaction_id) return ''
  return `${MANAGE_ENHANCED_DATA_PAGE_ROUTE}?tranxid=${transaction_id}`
}

const buildMaskedCardFromLast4 = (card_last4?: string, padLength: number = 5): string => {
  if (!card_last4) return ''
  return card_last4?.padStart(card_last4?.length + padLength, '*')
}

const getIsManualFillCompleted = (options: {
  card_level?: number,
  is_level2_added?: boolean,
  is_level3_added?: boolean
}): boolean => {
  const { card_level = 1, is_level2_added = false, is_level3_added = false } = options
  if (card_level === 2 && is_level2_added) return true
  if (card_level === 3 && is_level2_added && is_level3_added) return true
  return card_level === 1 && (is_level2_added || is_level3_added)
}

const buildNavigationTitleAndIcon = (options: {
  card_level: number, isManualFillCompleted: boolean
}): { icon: React.JSX.Element, title: string, title_style?: React.CSSProperties} => {
  const { card_level, isManualFillCompleted } = options
  const warning_link_style: React.CSSProperties = {
    color: 'rgb(225, 102, 66)',
  }
  const green_link_style: React.CSSProperties = {
    color: 'rgb(59, 118, 61)',
  }

  if (card_level === 3) {
    return isManualFillCompleted
      ? { icon: <CheckSolidIcon />, title: 'Edit Data' }
      : { icon: <WarningSolidIcon />, title: 'Add Data', title_style: warning_link_style }
  }

  return isManualFillCompleted
    ? { icon: <CheckSolidIcon />, title: 'Edit Data', title_style: green_link_style }
    : { icon: <CheckHollowIcon />, title: 'Add Data', title_style: green_link_style }
}

const buildNavigationAnchor = (
  transaction_id: string,
  card_level: number,
  isManualFillCompleted: boolean,
  onClick: (e: React.MouseEvent<HTMLAnchorElement>) => void
): React.JSX.Element => {
  const href: string = buildNavigationURLForMEDPage(transaction_id)
  const { icon, title, title_style } = buildNavigationTitleAndIcon({ card_level, isManualFillCompleted })
  return <NavigationAnchor
    className={'strong-anchor'}
    href={href}
    title={title}
    icon={icon}
    onClick={onClick}
    metadata={{ 'data-card-level': card_level }}
    style={title_style}
  />
}

const mapToTransactionRowData = (
  tranData: EDT.TransactionSearchRecordForManualFill,
  onClickHref: (e: React.MouseEvent<HTMLAnchorElement>) => void
): RowDataWithKey<EDT.EDTransactionRowData> => {
  const {
    legacy_record_id = '', settle_amount = 0, card_last4, card_brand, billing_name,
    card_level = 1, is_level2_added, is_level3_added,
  } = tranData

  const isManualFillCompleted = getIsManualFillCompleted({ card_level, is_level2_added, is_level3_added })
  const navigationHref = buildNavigationAnchor(legacy_record_id, card_level, isManualFillCompleted, onClickHref)
  return {
    card_level,
    isManualFillCompleted,
    amount: settle_amount,
    card_masked: buildMaskedCardFromLast4(card_last4),
    name: billing_name || '',
    card_brand: card_brand || '',
    transaction_id: legacy_record_id || '',
    navigation_href: navigationHref,
    key: legacy_record_id || '',
  }
}

export { mapToTransactionRowData }
